import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ──────────────────────────────────────────────────────────────────
// Token storage — pre-fix this used sessionStorage which Safari ITP
// wipes aggressively (private browsing, tab suspension, cross-tab),
// causing the "kicked out on every click" reports. We now:
//   1. Persist the access_token in localStorage (30-day survival).
//   2. Send the httpOnly `access_token` cookie alongside every
//      request via `axios.defaults.withCredentials = true`. If
//      localStorage somehow gets wiped, the cookie keeps the user
//      authenticated.
//   3. Keep a sessionStorage MIRROR so multiple tabs share state
//      without each having to hit /auth/me.
// ──────────────────────────────────────────────────────────────────
const TOKEN_KEY = 'shopperden_access_token';
const _encode = (v) => { try { return btoa(v); } catch { return v; } };
const _decode = (v) => { try { return atob(v); } catch { return v; } };
const tokenStore = {
  get: () => {
    try {
      const sessionVal = sessionStorage.getItem(TOKEN_KEY);
      if (sessionVal) return _decode(sessionVal);
      const localVal = localStorage.getItem(TOKEN_KEY);
      if (localVal) {
        // Backfill sessionStorage so the rest of this tab is fast.
        try { sessionStorage.setItem(TOKEN_KEY, localVal); } catch (_e) { /* private mode */ }
        return _decode(localVal);
      }
      // Last-ditch legacy migration.
      const legacy = localStorage.getItem('access_token');
      if (legacy) { tokenStore.set(legacy); return legacy; }
    } catch (_e) { /* storage unavailable */ }
    return null;
  },
  set: (token) => {
    const encoded = _encode(token);
    try { sessionStorage.setItem(TOKEN_KEY, encoded); } catch (_e) { /* ignore */ }
    try { localStorage.setItem(TOKEN_KEY, encoded); } catch (_e) { /* ignore */ }
    try { localStorage.removeItem('access_token'); } catch (_e) { /* ignore */ }
  },
  clear: () => {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (_e) { /* ignore */ }
    try { localStorage.removeItem(TOKEN_KEY); } catch (_e) { /* ignore */ }
    try { localStorage.removeItem('access_token'); } catch (_e) { /* ignore */ }
  },
};

// Send the httpOnly cookie alongside every API call so the backend
// can authenticate even when localStorage has been wiped (Safari ITP,
// Incognito, etc.). The pre-fix flow IGNORED the cookie entirely.
axios.defaults.withCredentials = true;

// Axios interceptor — Bearer header from storage.
axios.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const formatApiErrorDetail = (detail) => {
    // Map the most common backend rejections to clear human copy. Without
    // this, an invalid-password 401 was being shown as the generic
    // "Something went wrong" toast, leading users to think the system was
    // broken when really their password was simply wrong.
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') {
      const lc = detail.toLowerCase();
      if (lc.includes('invalid credentials') || lc === 'unauthorized')
        return 'Wrong email or password. Use "Forgot password?" if needed.';
      if (lc.includes('not found') && lc.includes('email'))
        return 'No account found with that email. Try signing up instead.';
      if (lc.includes('locked') || lc.includes('too many'))
        return 'Too many attempts. Please wait a few minutes and try again.';
      if (lc.includes('not verified'))
        return 'Email not verified yet. Check your inbox for the verification link.';
      return detail;
    }
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Pre-fix this cleared the token on ANY error, including a
      // single 502 / network blip / timeout — that was the "kicked
      // out on every click" bug. Only clear on a genuine 401
      // (unauthorised). Everything else keeps the user logged in
      // and the next request retries automatically.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        tokenStore.clear();
        setUser(false);
      } else {
        // Transient — keep prior user state (which is null on first
        // load and a real user object on refresh).
        logger.warn?.('checkAuth transient error, keeping session:', status, error?.message);
        // If we have no user yet, we still need to render the app —
        // set false so login screen shows, but do NOT clear tokens.
        setUser((prev) => (prev ? prev : false));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check so AuthCallback
    // can exchange the session_id first. Per Emergent Auth playbook.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (response.data.token) {
        tokenStore.set(response.data.token);
      }
      setUser(response.data);
      return { success: true, role: response.data.role };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  }, []);

  const register = useCallback(async (email, password, name = '', extraData = {}) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, ...extraData },
      );
      if (response.data.token) {
        tokenStore.set(response.data.token);
      }
      setUser(response.data);
      return { success: true, role: response.data.role };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      tokenStore.clear();
      try { localStorage.removeItem('shopperden_last_auth'); } catch (_e) { /* ignore */ }
      setUser(false);
    }
  }, []);

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, checkAuth,
  }), [user, loading, login, register, logout, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
